// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import { LocationModel } from "@insite/client-framework/Common/Hooks/useLocationFilterSearch";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Dictionary } from "@insite/client-framework/Common/Types";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getFooter } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import { mapUserData } from "@stoneage/App/Components/MarketoFormModal/GetFilteredUserData";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import { getUserDataForMarketoForm } from "@stoneage/App/Store/Selectors/AccountSettingsSelectors";
import {
    getBillToCustomerNumber,
    trackAsImpersonation,
    trackGaBookConsultationEvent,
    trackGaContactUsEvent,
    trackGaNewsSubscriptionEvent,
    trackGaRequestQuoteEvent,
    trackGaRequestRentEvent,
    trackGaSearchDealerEvent,
} from "@stoneage/App/Utilities/DataLayer";
import { constants } from "@stoneage/XDL/constants";
import parse from "html-react-parser";
import React, { useEffect, useRef, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { css } from "styled-components";

export interface MarketoFormWrapperStyles {
    formWrapper?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
}

export const styles: MarketoFormWrapperStyles = {
    formWrapper: {
        css: css`
            ${({ width }: any) => {
                if (width) {
                    return css`
                        max-width: ${width}px!important;
                        width: 100%;
                        overflow-x: auto;
                    `;
                }
            }}

            ${({ height }: any) => {
                if (height) {
                    return css`
                        max-height: ${height}px!important;
                        height: 100%;
                        overflow-y: auto;
                    `;
                }
            }}
            ${({ customCss }: any) => {
                if (customCss) {
                    return css`
                        ${customCss}
                    `;
                }
            }}
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
};

type OwnProps = {
    formWidth?: number;
    formHeight?: number;
    formId: string;
    customCss?: string;
    style?: React.CSSProperties;
    // InModal flag is true only when the component is inside `MarketoCustomModal`
    inModal: boolean;
    confirmationMessage?: string;
    onUuidGenerate?: (uuid: string) => void;
    extraLocationData?: {
        location: LocationModel & { properties: Dictionary<string> };
        comingFromDealerPage: boolean;
        inShell: boolean;
    };
    pdpItem?: string;
    // We use formType to determine the google analytics event triggering on success
    formType?: "contactUs" | "requestQuote" | "requestRent" | "searchDealer" | "newsSubscription" | "bookConsultation";
    // This flag is usally used for each instance that is outside of the `MarketoCustomModal` component
    // which forces the marketo forms to be initlized and re-rendered by Render Function inside Marketo Instance
    useRenderFunction: boolean;
};

const mapStateToProps = (state: ApplicationState) => {
    const widgetFields = getWidgetsByPageId(state, getFooter(state).id).find((widget: WidgetProps) => {
        return widget.type === "MarketoForm/MarketoFormInitializer";
    })?.fields;
    return {
        loadedMarketoForms: (state as CustomAppState).xMarketoModalForms,
        fieldsMapping: widgetFields?.fieldsMapping,
        userData: getUserDataForMarketoForm(state as CustomAppState),
        activeFormData: (state as CustomAppState).xMarketoModalForms.activeForm,
        customerNumber: getBillToCustomerNumber(state),
        impersonation: trackAsImpersonation(state),
        contactUsEvent: (state as CustomAppState).xGoogleAnalytics.contactUsEvent,
        requestQuoteEvent: (state as CustomAppState).xGoogleAnalytics.requestQuoteEvent,
        requestRentEvent: (state as CustomAppState).xGoogleAnalytics.requestRentEvent,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const MarketoFormWrapper = ({
    formWidth,
    formHeight,
    formId,
    customCss,
    style,
    inModal,
    onUuidGenerate,
    confirmationMessage,
    extraLocationData,
    formType,
    useRenderFunction,
    pdpItem,
    loadedMarketoForms,
    fieldsMapping,
    userData,
    activeFormData,
    customerNumber,
    impersonation,
    contactUsEvent,
    requestQuoteEvent,
    requestRentEvent,
}: Props) => {
    const formRef = useRef<HTMLFormElement>(null);
    const divRef = useRef<HTMLDivElement>(null);
    const dispatch = useDispatch();

    const visibleFormStyles: React.CSSProperties = {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        width: "auto",
        height: "auto",
    };

    const [currentStyles, setCurrentStyles] = useState<React.CSSProperties>(visibleFormStyles);
    const [successMessage, setSuccessMessage] = useState<string | undefined>("");
    const [uuid, setUuid] = useState("");
    const [locationData, setLocationData] = useState(extraLocationData);
    const [showConfirmMessage, setShowConfirmMessage] = useState(false);
    const userIdentifier = customerNumber || uniqueId();

    useEffect(() => {
        if (extraLocationData !== undefined) {
            setLocationData(extraLocationData);
        }
    }, [extraLocationData?.inShell]);

    useEffect(() => {
        const uuid = uniqueId();
        if (onUuidGenerate) {
            onUuidGenerate(uuid);
        }
        setUuid(uuid);
        if (confirmationMessage) {
            setSuccessMessage(confirmationMessage);
        }
    }, []);

    useEffect(() => {
        const mkInstance = (window as any).MktoForms2;
        const totalLoadedForms = loadedMarketoForms.totalLoadedForms;
        const allForms = loadedMarketoForms.totalForms;

        if (useRenderFunction && mkInstance && totalLoadedForms === allForms) {
            setTimeout(() => {
                const form = mkInstance.getForm(formId);
                if (form) {
                    const mappedData = locationData
                        ? mapUserData(
                              userData,
                              fieldsMapping,
                              locationData.inShell,
                              locationData.location,
                              locationData.comingFromDealerPage,
                          )
                        : mapUserData(userData, fieldsMapping);

                    // XNG-Change: track GA4
                    switch (formType) {
                        case "contactUs":
                            form.onSuccess(() => {
                                contactUsEvent && trackGaContactUsEvent(userIdentifier, impersonation, customerNumber);
                            });
                            break;
                        case "requestQuote":
                            form.onSuccess(() => {
                                const storedPartNumber = localStorage.getItem(constants.cookies.productPartNumber);
                                const splittedPartNumbers = storedPartNumber?.split(",");
                                if (splittedPartNumbers && splittedPartNumbers.length > 1) {
                                    // clear checked items after submitting form
                                    dispatch({ type: "xProductBomAction/reset" });
                                }
                                storedPartNumber === mappedData["Part_ID__c"] &&
                                    requestQuoteEvent &&
                                    trackGaRequestQuoteEvent(
                                        {
                                            productTitle: pdpItem || "",
                                            productNumber: mappedData["Part_ID__c"],
                                        },
                                        userIdentifier,
                                        impersonation,
                                        customerNumber,
                                    );
                            });
                            break;
                        case "requestRent":
                            form.onSuccess(() => {
                                requestRentEvent &&
                                    trackGaRequestRentEvent(
                                        {
                                            productTitle: pdpItem || "",
                                            productNumber: mappedData["Part_ID__c"],
                                        },
                                        form.vals()?.["Email"],
                                        impersonation,
                                        customerNumber,
                                    );
                            });
                            break;
                        case "newsSubscription":
                            form.onSuccess(() => {
                                trackGaNewsSubscriptionEvent(form.vals()?.["Email"], impersonation, customerNumber);
                            });
                            break;
                        case "bookConsultation":
                            form.onSuccess(() => {
                                trackGaBookConsultationEvent(form.vals()?.["Email"], impersonation, customerNumber);
                            });
                            break;
                        case "searchDealer":
                            form.onSuccess(() => {
                                trackGaSearchDealerEvent(
                                    form.vals()?.["Email"],
                                    mappedData["Dealer_Namec"],
                                    impersonation,
                                    customerNumber,
                                );
                            });
                            break;
                    }
                    const formEl: HTMLFormElement = form.render()[0];

                    if (formEl) {
                        [...formEl.getElementsByTagName("input")].forEach((input: HTMLInputElement) => {
                            const inputName = input.name;
                            if (mappedData[inputName]) {
                                input.value = mappedData[inputName];
                            }
                        });
                    }

                    if (divRef.current) {
                        formEl.style.visibility = "visible";
                        formEl.style.position = "static";
                        setCurrentStyles(visibleFormStyles);
                        const prevForm = divRef.current.querySelector("form");
                        if (prevForm) {
                            divRef.current.removeChild(prevForm);
                        }

                        divRef.current.appendChild(formEl);
                    }
                }
            }, 600);
        }
    }, [loadedMarketoForms.totalLoadedForms]);

    useEffect(() => {
        setShowConfirmMessage(activeFormData.formId === formId && activeFormData.formUuid === uuid);
    }, [activeFormData.formId, activeFormData.formUuid]);

    return (
        <>
            <StyledWrapper
                {...styles.formWrapper}
                {...{ width: formWidth, height: formHeight, customCss }}
                id={`Marketo_FormWrapper_${uuid}`}
                style={currentStyles}
                ref={divRef}
            >
                {loadedMarketoForms.marketoLoading && (
                    <StyledWrapper {...styles.centeringWrapper}>
                        <LoadingSpinner {...styles.spinner} />
                    </StyledWrapper>
                )}
                {inModal && <form className="mktoForm" data-formid={formId} data-uuid={uuid} ref={formRef}></form>}
            </StyledWrapper>
            {successMessage && showConfirmMessage && (
                <SuccessMessage message={successMessage} formId={formId} uuid={uuid} />
            )}
        </>
    );
};

type SuccessMessageProps = {
    message: string;
    formId: string;
    uuid: string;
};

const SuccessMessage = ({ message, formId, uuid }: SuccessMessageProps) => {
    return <StyledWrapper id={`confirmform_${formId}_${uuid}`}>{parse(message, parserOptions)}</StyledWrapper>;
};

export default connect(mapStateToProps)(MarketoFormWrapper);
